import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  CallToActionButton,
  Container,
  Divider,
  FlexBox,
  InputCheckbox,
  Modal,
  PageStateMessage,
  Panel,
  Spacer,
  TextBody,
  TextCaption,
  TextDense,
  TextH2
} from '@directsoftware/ui-kit-web-admin';
import {
  fetchWlsContract,
  postWlsContractSign
} from '../../utils/wls-api-client';
import isWlsBookingNotFoundError from '../../utils/wls-booking-error';
import {
  ESIGN_CONSENT_TEXT,
  ESIGN_DISCLOSURE_TITLE,
  ESIGN_DISCLOSURE_FACT,
  ESIGN_DISCLOSURE_SECTIONS,
  buildContractSignPayload,
  contractSigningState,
  signatureRequired,
  photoRequired,
  alreadySignedError,
  signFailureMessage,
  formatContractTimestamp,
  renderContractTerms,
  verificationNavigationUrl
} from '../../utils/contract-signing';
import { stampAddendumPreview } from '../../utils/contract-addendum';
import { getBrandLogoUrl } from '../../../shared/brandThemeColors';
import SignatureCaptureV2 from '../inputs/signature-capture-v2';
import IdPhotoUploader from '../inputs/id-photo-uploader';
import AddendumFields from './addendum-fields';

const ContractV2 = props => {
  const brand = useSelector(state => state.brand);
  const bookingCode = props.match.params.booking_code;
  const [contract, setContract] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [values, setValues] = useState({});
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const generation = useRef(0);
  const signed = contractSigningState(contract, result) === 'signed';
  const logoUrl = (getBrandLogoUrl(brand) || '').split('?')[0];
  const orgId = brand.organization.id;

  useEffect(
    () => {
      mounted.current = true;
      generation.current += 1;
      inFlight.current = false;
      setSubmitting(false);
      setError('');
      let active = true;
      setLoading(true);
      setContract(null);
      setResult(null);
      setNotFound(false);
      setLoadError(false);
      setConsent(false);
      setShowDisclosure(false);
      setTermsAccepted(false);
      setSignature(null);
      setPhoto(null);
      setValues({});
      fetchWlsContract(brand, bookingCode)
        .then(response => {
          if (!active) return;
          if (!response.data || !response.data.booking_code) setNotFound(true);
          else setContract(response.data);
        })
        .catch(failure => {
          if (!active) return;
          if (isWlsBookingNotFoundError(failure)) setNotFound(true);
          else setLoadError(true);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
        mounted.current = false;
        generation.current += 1;
      };
    },
    [bookingCode, orgId]
  );

  useEffect(
    () => {
      if (!signed || (contract && contract.contract_pdf_url)) return undefined;
      let active = true;
      let timer;
      let attempts = 0;
      const poll = () => {
        attempts += 1;
        fetchWlsContract(brand, bookingCode)
          .then(response => {
            if (active && response.data && response.data.already_signed) {
              setContract(response.data);
              if (response.data.contract_pdf_url) active = false;
            }
          })
          .catch(() => {})
          .finally(() => {
            if (active && attempts < 10) timer = setTimeout(poll, 3000);
          });
      };
      poll();
      return () => {
        active = false;
        clearTimeout(timer);
      };
    },
    [signed, Boolean(contract && contract.contract_pdf_url), bookingCode, orgId]
  );

  const terms =
    contract && (contract.terms_and_conditions || contract.contract_body);
  const needsTerms = Boolean(
    terms ||
      (contract &&
        (contract.rental_agreement_url || contract.rental_agreement_text))
  );
  let payload;
  let validationError;
  if (contract) {
    try {
      payload = buildContractSignPayload(contract, {
        consent,
        signature: signature && signature.url,
        photo: photo && photo.data_uri,
        values
      });
    } catch (failure) {
      validationError = failure.message;
    }
  }

  const submit = async () => {
    if (inFlight.current || !payload || (needsTerms && !termsAccepted)) return;
    inFlight.current = true;
    const requestGeneration = generation.current;
    const isCurrent = () =>
      mounted.current && generation.current === requestGeneration;
    setSubmitting(true);
    setError('');
    try {
      const response = await postWlsContractSign(brand, payload);
      if (!response.data || response.data.success !== true)
        throw new Error('Signing did not complete. Please try again.');
      if (isCurrent()) setResult(response.data);
    } catch (failure) {
      if (!isCurrent()) return;
      console.error('Contract signing request failed');
      if (alreadySignedError(failure)) {
        try {
          const response = await fetchWlsContract(brand, bookingCode);
          if (response.data && response.data.already_signed) {
            if (isCurrent()) setContract(response.data);
          } else if (isCurrent())
            setError('Unable to confirm signing. Please refresh the page.');
        } catch (lookupFailure) {
          if (isCurrent())
            setError('Unable to confirm signing. Please refresh the page.');
        }
      } else if (isCurrent()) {
        setError(signFailureMessage(failure));
      }
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setSubmitting(false);
      }
    }
  };

  if (loading)
    return (
      <Container variation="extended-wls">
        <TextBody>Loading agreement…</TextBody>
      </Container>
    );
  if (notFound || loadError)
    return (
      <Container variation="extended-wls">
        <PageStateMessage
          iconProps={{ name: notFound ? 'emptySet' : 'exclamationTriangle' }}
          headline={notFound ? 'Booking Not Found' : 'Unable to Load Booking'}
          description={
            notFound
              ? "We couldn't find a booking with that confirmation code. Please check the link and try again."
              : "We couldn't load your booking details. Please try again later."
          }
        />
      </Container>
    );

  const detailRow = (label, value) =>
    value ? (
      <FlexBox justifyContent="space-between" gap="m">
        <TextDense>{label}</TextDense>
        <TextDense weight="semibold">{value}</TextDense>
      </FlexBox>
    ) : null;
  const consentAt = formatContractTimestamp(
    (result && result.esign_consent_at) || contract.esign_consent_at
  );
  const signedAt = formatContractTimestamp(
    (result && result.signed_at) || contract.signed_at
  );
  const verificationPending =
    result && typeof result.verification_pending === 'boolean'
      ? result.verification_pending
      : contract.verification_pending;
  const verificationUrl = verificationNavigationUrl(
    contract.verification_url,
    window.location.origin
  );

  return (
    <Container variation="extended-wls">
      <Spacer size="l" />
      <FlexBox justifyContent="center">
        <Box style={{ maxWidth: '640px', width: '100%' }}>
          <Panel addShadow>
            <Box padding="l">
              {logoUrl && (
                <img
                  src={logoUrl}
                  onError={event => {
                    const { style } = event.currentTarget;
                    style.display = 'none';
                  }}
                  alt={brand.name}
                  style={{ maxHeight: '56px', maxWidth: '200px' }}
                />
              )}
              <TextH2>
                {signed ? 'Agreement Complete' : 'Rental Agreement'}
              </TextH2>
              <TextCaption>{contract.brand_name || brand.name}</TextCaption>
              <Spacer size="l" />
              {verificationPending && (
                <Panel>
                  <Box padding="m">
                    <TextH2>Identity verification is still required</TextH2>
                    {verificationUrl && (
                      <CallToActionButton
                        onClick={() => window.location.assign(verificationUrl)}
                      >
                        Complete identity verification
                      </CallToActionButton>
                    )}
                  </Box>
                </Panel>
              )}
              {signed ? (
                <>
                  <TextH2>Certificate of Completion</TextH2>
                  <Spacer />
                  {detailRow('Reference ID', contract.booking_code)}
                  {detailRow(
                    'Signer',
                    (result && result.signer_name) || contract.signer_name
                  )}
                  {detailRow(
                    'Electronic consent',
                    consentAt && `Captured ${consentAt}`
                  )}
                  {signatureRequired(contract) &&
                    detailRow('Signature method', 'Electronic signature')}
                  {detailRow('Signed on', signedAt)}
                  <Spacer />
                  {contract.contract_pdf_url ? (
                    <a
                      href={contract.contract_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download signed agreement
                    </a>
                  ) : (
                    <TextBody>
                      Your signed agreement is being prepared and will be
                      emailed to you.
                    </TextBody>
                  )}
                </>
              ) : (
                <>
                  <TextH2>Reservation Details</TextH2>
                  <Spacer />
                  {detailRow('Confirmation Code', contract.booking_code)}
                  {detailRow('Guest', contract.guest_name)}
                  {detailRow('Property', contract.property_name)}
                  {detailRow('Unit', contract.unit_name)}
                  {detailRow(
                    'Dates',
                    contract.check_in &&
                      contract.check_out &&
                      `${moment(contract.check_in).format(
                        'MMM D, YYYY'
                      )} — ${moment(contract.check_out).format('MMM D, YYYY')}`
                  )}
                  {detailRow('Guests', contract.num_guests)}
                  <Spacer />
                  <Divider />
                  <Spacer />
                  {terms && (
                    <div
                      className="customHtmlStyles -denseBody"
                      dangerouslySetInnerHTML={{
                        __html: stampAddendumPreview(
                          renderContractTerms(terms, contract),
                          values
                        )
                      }}
                    />
                  )}
                  {Array.isArray(contract.rental_agreement_text) && (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      <TextBody>
                        {contract.rental_agreement_text.join('\n\n')}
                      </TextBody>
                    </div>
                  )}
                  {contract.rental_agreement_url && (
                    <a
                      href={contract.rental_agreement_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View rental agreement
                    </a>
                  )}
                  <AddendumFields
                    fields={contract.addendum_fields}
                    values={values}
                    signature={signature}
                    onChange={setValues}
                  />
                  {needsTerms && (
                    <InputCheckbox
                      isChecked={termsAccepted}
                      onChange={() => setTermsAccepted(!termsAccepted)}
                    >
                      I have read and agree to the Terms and Conditions.
                    </InputCheckbox>
                  )}
                  {signatureRequired(contract) && (
                    <>
                      <Spacer />
                      <TextH2>E-Signature</TextH2>
                      <Spacer />
                      <SignatureCaptureV2
                        localOnly
                        signature={signature}
                        afterSave={setSignature}
                        afterDelete={() => setSignature(null)}
                      />
                    </>
                  )}
                  {photoRequired(contract) && (
                    <>
                      <Spacer />
                      <TextH2>Photo ID</TextH2>
                      <Spacer />
                      <IdPhotoUploader
                        localOnly
                        afterUpload={setPhoto}
                        afterPhotoDelete={() => setPhoto(null)}
                      />
                    </>
                  )}
                  <Spacer />
                  <InputCheckbox
                    isChecked={consent}
                    onChange={() => setConsent(!consent)}
                  >
                    {ESIGN_CONSENT_TEXT}
                  </InputCheckbox>
                  <a
                    href="#electronic-disclosure"
                    onClick={event => {
                      event.preventDefault();
                      setShowDisclosure(true);
                    }}
                  >
                    {ESIGN_DISCLOSURE_TITLE}
                  </a>
                  <Modal
                    title={ESIGN_DISCLOSURE_TITLE}
                    reveal={showDisclosure}
                    size="m"
                    closeOnClick={() => setShowDisclosure(false)}
                  >
                    <Box padding="m">
                      <TextBody>{ESIGN_DISCLOSURE_FACT}</TextBody>
                      {ESIGN_DISCLOSURE_SECTIONS.map(section => (
                        <Box key={section} paddingVertical="s">
                          <TextH2>{section}</TextH2>
                          <TextBody>Pending legal review</TextBody>
                        </Box>
                      ))}
                    </Box>
                  </Modal>
                  <Spacer />
                  {error && (
                    <div role="alert">
                      <TextBody>{error}</TextBody>
                    </div>
                  )}
                  {!submitting &&
                    (validationError || (needsTerms && !termsAccepted)) && (
                      <TextCaption>
                        {validationError ||
                          'Please read and agree to the terms.'}
                      </TextCaption>
                    )}
                  <CallToActionButton
                    onClick={submit}
                    isDisabled={
                      submitting ||
                      Boolean(validationError) ||
                      (needsTerms && !termsAccepted)
                    }
                    isFullWidth
                  >
                    {submitting ? 'Signing…' : 'Sign Rental Agreement'}
                  </CallToActionButton>
                </>
              )}
              <Spacer size="l" />
              <a
                href={`/my-bookings/receipt/${encodeURIComponent(bookingCode)}`}
              >
                Back to booking receipt
              </a>
            </Box>
          </Panel>
        </Box>
      </FlexBox>
      <Spacer size="xxl" />
    </Container>
  );
};
ContractV2.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ booking_code: PropTypes.string.isRequired })
      .isRequired
  }).isRequired
};
export default ContractV2;
